import { t } from '@lingui/macro';
import i18n from 'config/i18n';
import Config from 'config';
import { Months } from 'utils/time';
import Subscription from '../../models/subscription';

import { GET_CONFIGURATIONS } from '../app/actions';
import { LOGOUT } from '../auth/actions';
import { CLEAR_PLAN, LOAD_PLAN, LOAD_PLANS, LOAD_INVOICES, LOAD_SUBSCRIPTION, POST_PLAN, PUT_PLAN, SET_VALUE, PAYWALL_SET_VALUE, GET_PAYMENT_METHODS } from './actions';

const description = Config.app.APP_PAYWALL_DESCRIPTION || i18n._(t`With a premium plan you have access to all music available in the platform without ADs and in full control of your library.`);

const initialState = {
  /**
   * The lists of available plans
   */
  plans: [],

  /**
   * The current plan to edit by the admin
   */
  plan: {
    name: '',
    price: '',
    description: '',
    stripe_plan: '',
    highlighted: false,
    features: [
      i18n._(t`Ad Free`),
      i18n._(t`Unlimited Playlists`),
      i18n._(t`Listen Offline`),
      i18n._(t`Premium Badge`),
    ],
    recurring: '',
  },

  /**
   * Custom content for the paywall
   */
  paywall: {
    title: Config.app.APP_PAYWALL_TITLE || i18n._(t`Subscribe to our premium plans`),
    description,
    footer: Config.app.APP_PAYWALL_FOOTER || i18n._(t`All purchases are final.`),
  },

  /**
   * The current's user subscription plan
   */
  subscription: {
    name: i18n._(t`Forever Free`),
    description,
    stripe_plan: '',
    features: [],
  },

  // Stores the payment method for the current user
  payment: {
    primary: undefined,
    methods: [],
  },

  // Invoice history
  invoices: [],
};

export default function reducer(state = initialState, action) {
  switch(action.type) {
    case CLEAR_PLAN:
      return {
        ...state,
        plan: initialState.plan,
      };
    case LOAD_PLANS.SUCCESS:
      return {
        ...state,
        plans: action.payload.plans,
      };
    case LOAD_PLAN.SUCCESS:
      return {
        ...state,
        plan: action.payload.plan,
      };
    case POST_PLAN.SUCCESS:
      return {
        ...state,
        plan: {
          ...state.plan,
          id: action.payload.plan.id,
        },
      };
    case PUT_PLAN.SUCCESS:
      const plans = [...state.plans];
      const index = plans.findIndex(pln => pln.id === action.payload.plan.id);
      if (index > -1) {
        plans[index] = action.payload.plan;
      }
      return {
        ...state,
        plans,
        plan: action.payload.plan,
      };
    case SET_VALUE:
      return {
        ...state,
        plan: {
          ...state.plan,
          [action.payload.name]: action.payload.value,
        },
      };
    case PAYWALL_SET_VALUE:
      return {
        ...state,
        paywall: {
          ...state.paywall,
          [action.payload.name]: action.payload.value,
        },
      };
    case LOAD_SUBSCRIPTION.SUCCESS:
      if (action.payload.subscription) {
        const payload = action.payload;
        const subscription = new Subscription(payload.subscription);
        const date = new Date(subscription.next);
        const description = subscription.grace
          ? i18n._(t`Your plan will expire on ${Months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}. We won't charge you anymore.`)
          : i18n._(t`Your next bill is for $${payload.plan.price} on ${Months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}.`);

        return {
          ...state,
          subscription: {
            ...subscription,
            name: payload.plan.name,
            description,
            features: payload.plan.features,
            planId: payload.plan.id,
            stripe_plan: payload.plan.stripe_plan,
          },
        };
      }
      return state;
    case GET_PAYMENT_METHODS.SUCCESS:
      return {
        ...state,
        payment: {
          ...state.payment,
          primary: action.payload.primary,
          methods: action.payload.methods,
        },
      };
    case LOAD_INVOICES.SUCCESS:
      return {
        ...state,
        invoices: action.payload.invoices,
      };
    case GET_CONFIGURATIONS.SUCCESS:
      const data = {};
      action.payload.configurations.forEach(obj => {
        if (['APP_PAYWALL_TITLE', 'APP_PAYWALL_FOOTER', 'APP_PAYWALL_DESCRIPTION'].includes(obj.key)) {
          data[obj.key] = obj.value;
        }
      });
      return {
        ...state,
        paywall: {
          ...state.paywall,
          title: data.APP_PAYWALL_TITLE || initialState.paywall.title,
          description: data.APP_PAYWALL_DESCRIPTION || initialState.paywall.description,
          footer: data.APP_PAYWALL_FOOTER || initialState.paywall.footer,
        },
      };
    case LOGOUT:
      return {
        ...state,
        subscription: initialState.subscription,
        payment: initialState.payment,
        invoices: initialState.invoices,
      };
    default:
      return state;
  }
}
